import axios from "axios"
import { normalizeInfluencer } from "./normalizeInfluencer"
import { okara_api } from "../../utils/variables"

// Same separate UGC/socials intelligence backend as ugcExplorerApi.js (not
// the main `REACT_APP_APIKEY` host) - TOP_INFLUENCER_FILTERS.md's example URL
// is the same backend family.
const UGC_API_BASE_URL = okara_api
const TOP_INFLUENCER_ENDPOINT = "/top-influencer/"

const authHeaders = () => {
  const token = localStorage.getItem("authToken")
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// camelCase (filter-bar state, matching this codebase's JS convention) ->
// snake_case (the endpoint's query params, TOP_INFLUENCER_FILTERS.md §2).
// Same translation pattern as FILTER_PARAM_MAP in ugcExplorerApi.js. Keys
// already spelled the same both directions (`search`, `username`, `country`,
// `q`) need no entry here.
const FILTER_PARAM_MAP = {
  creatorSize: "creator_size",
  minFollowers: "min_followers",
  maxFollowers: "max_followers",
  minPosts: "min_posts",
  maxPosts: "max_posts",
  minTotalViews: "min_total_views",
  maxTotalViews: "max_total_views",
  minPostsAboutApps: "min_posts_about_apps",
  appId: "app_id",
  appIds: "app_ids",
  verifiedOnly: "verified_only",
  hasEmail: "has_email",
  hasLinks: "has_links",
  hasDescription: "has_description",
  excludePrivate: "exclude_private",
  mirroredImageOnly: "mirrored_image_only",
  discoveredWithinDays: "discovered_within_days",
  refreshedWithinDays: "refreshed_within_days",
  staleOnly: "stale_only",
}

// Same shape as ugcExplorerApi.js's asApiError - normalizes an axios error
// into something the UI can branch on (`.status`/`.data`) directly. Per §1
// this endpoint never 400s on a bad filter, so in practice this only ever
// sees a genuine network/auth failure.
const asApiError = (error) => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  const err = new Error(data.error || error.message || "Failed to load influencers.")
  err.status = status
  err.data = data
  return err
}

// §5 - the envelope's list/counts are `influencers`/`total_influencers`/
// `total_influencers_unfiltered`; normalizing each row here means the rest of
// the app never has to reach into the raw shape directly.
const normalizeTopInfluencerResponse = (raw) => ({
  ...raw,
  influencers: (raw.influencers || []).map(normalizeInfluencer),
})

/**
 * Fetch one page of the cross-platform Top Influencer leaderboard
 * (TOP_INFLUENCER_FILTERS.md). Unlike the video endpoints this isn't scoped
 * to a single app - `filters.appId` is just one more optional narrowing
 * filter (§2 "App coverage"), never required - and there's no discovery job
 * to poll: every response is already-settled data, open to every access
 * level including guests (§1).
 */
export async function fetchTopInfluencers({
  page = 1,
  pageSize = 20,
  sort = "audience",
  includeFacets = false,
  includeBreakdown = false,
  filters = {},
} = {}) {
  const params = { page, page_size: pageSize, sort }
  if (includeFacets) params.include_facets = true
  if (includeBreakdown) params.include_breakdown = true

  // Same "never 400s, unrecognised values are just dropped" contract as the
  // video endpoints (§1) - safe to forward filter state without validating
  // shapes here. Empty/false/undefined values are dropped client-side so
  // they don't show up in `filters_applied` as noise.
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === false) return
    if (Array.isArray(value) && value.length === 0) return
    const paramKey = FILTER_PARAM_MAP[key] || key
    params[paramKey] = Array.isArray(value) ? value.join(",") : value
  })

  try {
    const response = await axios.get(`${UGC_API_BASE_URL}${TOP_INFLUENCER_ENDPOINT}`, {
      params,
      headers: authHeaders(),
    })
    return normalizeTopInfluencerResponse(response.data)
  } catch (error) {
    throw asApiError(error)
  }
}
